import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { StockDataPoint } from '../data/mockStockData';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface DateRangeNavigatorProps {
  data: StockDataPoint[];
  selectedRange: [Date, Date];
  onRangeChange: (startDate: Date, endDate: Date) => void;
  theme: 'light' | 'dark';
  symbol: string;
}

export const DateRangeNavigator: React.FC<DateRangeNavigatorProps> = ({
  data,
  selectedRange,
  onRangeChange,
  theme,
  symbol
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | 'range' | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  // Calculate positions based on date range
  const getPositionFromDate = (date: Date): number => {
    if (data.length === 0) return 0;
    const startTime = data[0].timestamp.getTime();
    const endTime = data[data.length - 1].timestamp.getTime();
    const targetTime = date.getTime();
    return ((targetTime - startTime) / (endTime - startTime)) * 100;
  };

  const getDateFromPosition = useCallback((position: number): Date => {
    if (data.length === 0) return new Date();
    const startTime = data[0].timestamp.getTime();
    const endTime = data[data.length - 1].timestamp.getTime();
    const targetTime = startTime + (position / 100) * (endTime - startTime);
    return new Date(targetTime);
  }, [data]);

  const startPosition = getPositionFromDate(selectedRange[0]);
  const endPosition = getPositionFromDate(selectedRange[1]);

  // Chart data for mini navigator
  const chartData = useMemo(() => ({
    datasets: [
      {
        label: `${symbol} Overview`,
        data: data.map(point => ({
          x: point.timestamp,
          y: point.close
        })),
        borderColor: theme === 'dark' ? '#60A5FA' : '#3B82F6',
        backgroundColor: 'transparent',
        borderWidth: 1,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.1
      }
    ]
  }), [data, symbol, theme]);

  // Chart options for mini navigator
  const chartOptions: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    scales: {
      x: {
        type: 'time',
        display: true,
        grid: {
          display: false
        },
        ticks: {
          display: true,
          maxTicksLimit: 6,
          color: theme === 'dark' ? '#6B7280' : '#9CA3AF',
          font: {
            size: 10
          }
        }
      },
      y: {
        display: false,
        grid: {
          display: false
        }
      }
    },
    elements: {
      point: {
        radius: 0
      }
    }
  }), [theme]);

  // Handle mouse events
  const handleMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'range') => {
    e.preventDefault();
    setIsDragging(type);
    
    if (type === 'range') {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const clickX = e.clientX - rect.left;
        const clickPercent = (clickX / rect.width) * 100;
        setDragOffset(clickPercent - startPosition);
      }
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (mouseX / rect.width) * 100));

    let newStartPosition = startPosition;
    let newEndPosition = endPosition;

    if (isDragging === 'start') {
      newStartPosition = Math.min(percentage, endPosition - 5);
    } else if (isDragging === 'end') {
      newEndPosition = Math.max(percentage, startPosition + 5);
    } else if (isDragging === 'range') {
      const rangeWidth = endPosition - startPosition;
      newStartPosition = Math.max(0, Math.min(100 - rangeWidth, percentage - dragOffset));
      newEndPosition = newStartPosition + rangeWidth;
    }

    const newStartDate = getDateFromPosition(newStartPosition);
    const newEndDate = getDateFromPosition(newEndPosition);
    
    onRangeChange(newStartDate, newEndDate);
  }, [isDragging, startPosition, endPosition, dragOffset, getDateFromPosition, onRangeChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
    setDragOffset(0);
  }, []);

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const styles = {
    container: {
      position: 'relative' as const,
      height: '7.5rem',
      backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
      borderRadius: '0.5rem',
      padding: '0.625rem',
      marginTop: '20px',
      userSelect: 'none' as const,
      cursor: isDragging ? 'grabbing' : 'default',
    },
    chartContainer: {
      position: 'relative' as const,
      height: '3.75rem',
      marginBottom: '10px',
    },
    sliderContainer: {
      position: 'relative' as const,
      height: '1.875rem',
      backgroundColor: theme === 'dark' ? '#1F2937' : '#F3F4F6',
      borderRadius: '0.25rem',
      margin: '10px 0',
    },
    selectedRange: {
      position: 'absolute' as const,
      top: '0',
      height: '100%',
      backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
      borderRadius: '0.25rem',
      left: `${startPosition}%`,
      width: `${endPosition - startPosition}%`,
      cursor: 'grab',
    },
    handle: {
      position: 'absolute' as const,
      top: '-2px',
      width: '12px',
      height: '2.125rem',
      backgroundColor: theme === 'dark' ? '#3B82F6' : '#2563EB',
      borderRadius: '0.375rem',
      cursor: 'ew-resize',
      border: `2px solid ${theme === 'dark' ? '#1F2937' : '#FFFFFF'}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    startHandle: {
      left: `${startPosition}%`,
      transform: 'translateX(-50%)',
    },
    endHandle: {
      left: `${endPosition}%`,
      transform: 'translateX(-50%)',
    },
    dateLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.75rem',
      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
      marginTop: '5px',
    },
    selectedDates: {
      fontSize: '0.6875rem',
      color: theme === 'dark' ? '#60A5FA' : '#2563EB',
      fontWeight: '600',
    }
  };

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Mini Chart */}
      <div style={styles.chartContainer}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Range Slider */}
      <div style={styles.sliderContainer}>
        {/* Selected Range Background */}
        <div
          style={{
            ...styles.selectedRange,
            ...(isDragging === 'range' ? { cursor: 'grabbing' } : {})
          }}
          onMouseDown={(e) => handleMouseDown(e, 'range')}
        />
        
        {/* Start Handle */}
        <div
          style={{
            ...styles.handle,
            ...styles.startHandle,
            ...(isDragging === 'start' ? { backgroundColor: theme === 'dark' ? '#1D4ED8' : '#1E40AF' } : {})
          }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
        />
        
        {/* End Handle */}
        <div
          style={{
            ...styles.handle,
            ...styles.endHandle,
            ...(isDragging === 'end' ? { backgroundColor: theme === 'dark' ? '#1D4ED8' : '#1E40AF' } : {})
          }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
        />
      </div>

      {/* Date Labels */}
      <div style={styles.dateLabels}>
        <span>
          {data.length > 0 ? data[0].timestamp.toLocaleDateString('he-IL') : ''}
        </span>
        <span style={styles.selectedDates}>
          {selectedRange[0].toLocaleDateString('he-IL')} - {selectedRange[1].toLocaleDateString('he-IL')}
        </span>
        <span>
          {data.length > 0 ? data[data.length - 1].timestamp.toLocaleDateString('he-IL') : ''}
        </span>
      </div>
    </div>
  );
};





